/**
 * Seamless Mobile Safari toolbar tinting.
 *
 * Keeps html/body backgrounds, fixed edge strips, and theme-color in sync with
 * the solid colors at the visible top and bottom of the page.
 * @see https://joshpuckett.me/seamless-mobile-safari
 */

const EDGE_CANDIDATES =
    '.section, [data-section-type], [data-nav-theme], footer, .site-footer, main > *';

let themeColorMeta = null;
let scheduled = false;
let lastTop = '';
let lastBottom = '';

function normalizeHex(value) {
    const raw = typeof value === 'string' ? value.trim() : '';
    if (!raw) return null;
    const prefixed = raw.startsWith('#') ? raw : `#${raw}`;
    return /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(prefixed) ? prefixed : null;
}

function rgbToHex(rgb) {
    if (typeof rgb !== 'string') return null;
    const match = rgb.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
    if (!match) return null;
    const toHex = (n) => Number(n).toString(16).padStart(2, '0');
    return `#${toHex(match[1])}${toHex(match[2])}${toHex(match[3])}`;
}

function isTransparent(color) {
    if (!color) return true;
    const value = color.trim().toLowerCase();
    return value === 'transparent' || value === 'rgba(0, 0, 0, 0)';
}

function getBodyFallback() {
    const bodyBg = getComputedStyle(document.body).getPropertyValue('--color-bg').trim();
    if (bodyBg && !bodyBg.startsWith('var(')) return bodyBg;
    const computed = getComputedStyle(document.body).backgroundColor;
    return isTransparent(computed) ? '#000000' : computed;
}

function resolveColorFromElement(el) {
    if (!el) return getBodyFallback();

    const custom = normalizeHex(el.getAttribute?.('data-nav-bg'));
    if (custom) return custom;

    let node = el;
    while (node && node !== document.documentElement) {
        const style = getComputedStyle(node);
        const token = style.getPropertyValue('--color-bg').trim();
        if (token && !token.startsWith('var(') && !isTransparent(token)) {
            return token;
        }
        const bg = style.backgroundColor;
        if (!isTransparent(bg)) {
            return bg;
        }
        node = node.parentElement;
    }

    return getBodyFallback();
}

function findSectionAtY(y) {
    const candidates = document.querySelectorAll(EDGE_CANDIDATES);
    let match = null;

    candidates.forEach((el) => {
        const rect = el.getBoundingClientRect();
        if (rect.height <= 0) return;
        if (rect.top <= y && rect.bottom >= y) {
            match = el;
        }
    });

    return match;
}

function ensureThemeColorMeta() {
    if (themeColorMeta) return themeColorMeta;
    themeColorMeta = document.querySelector('meta[name="theme-color"]');
    if (!themeColorMeta) {
        themeColorMeta = document.createElement('meta');
        themeColorMeta.setAttribute('name', 'theme-color');
        document.head.appendChild(themeColorMeta);
    }
    return themeColorMeta;
}

function applyEdgeColors(topColor, bottomColor) {
    if (topColor === lastTop && bottomColor === lastBottom) return;
    lastTop = topColor;
    lastBottom = bottomColor;

    const root = document.documentElement;
    root.style.setProperty('--edge-top-background', topColor);
    root.style.setProperty('--edge-bottom-background', bottomColor);

    const meta = ensureThemeColorMeta();
    meta.setAttribute('content', rgbToHex(topColor) || topColor);
}

function updateEdges() {
    scheduled = false;

    const viewportHeight = window.visualViewport?.height || window.innerHeight || 0;
    const topY = 1;
    const bottomY = Math.max(1, viewportHeight - 1);

    const topColor = resolveColorFromElement(findSectionAtY(topY));
    const bottomColor = resolveColorFromElement(findSectionAtY(bottomY));

    applyEdgeColors(topColor, bottomColor);
}

function scheduleUpdate() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(updateEdges);
}

function initSafariEdges() {
    if (!document.querySelector('.edge-top') || !document.querySelector('.edge-bottom')) {
        return;
    }

    updateEdges();

    window.addEventListener('scroll', scheduleUpdate, {passive: true});
    window.addEventListener('resize', scheduleUpdate);
    window.addEventListener('orientationchange', scheduleUpdate);
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') scheduleUpdate();
    });

    if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', scheduleUpdate);
        window.visualViewport.addEventListener('scroll', scheduleUpdate);
    }

    // Late-loading theme/section paint
    window.addEventListener('load', scheduleUpdate);
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSafariEdges);
} else {
    initSafariEdges();
}
